import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, PackageCheck } from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { cn, formatNumber } from "@/lib/utils";
import { formatFeedbackDate as formatDateTime } from "@/components/feedback/feedback-ui";

/** Trạng thái phiếu đóng gói (bộ phận Sản xuất) */
const PACKING_STATUS: Record<string, { label: string; cls: string }> = {
  PENDING: { label: "Chờ đóng gói", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  PACKED: { label: "Đã nhập kho", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

/**
 * Tab "Nhật ký đóng gói" của Lệnh sản xuất: bảng chỉ đọc các Phiếu đóng gói (Sản xuất) của lệnh —
 * mỗi phiếu nhận một lô KCS Đạt. Ghi nhận tại Xưởng sản xuất › Đóng gói & Nhập kho.
 */
export function POPackagingLogTab({ productionOrderId }: { productionOrderId?: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["packingSlips", "by-po", productionOrderId],
    enabled: !!productionOrderId,
    queryFn: async () => (trpcClient as any).packingSlips.list.query({ productionOrderId, page: 1, pageSize: 200 }),
  });
  const rows: any[] = data?.items || [];
  const sum = (pick: (r: any) => number) => rows.reduce((a, r) => a + (Number(pick(r)) || 0), 0);

  return (
    <div data-testid="po-packaging-log" className="bg-white border border-slate-200 rounded-xl shadow-sm">
      <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <PackageCheck className="h-4 w-4 text-indigo-600" />
          <h3 className="text-sm font-bold text-slate-800">Nhật ký đóng gói</h3>
          <span className="text-xs text-slate-500">({rows.length} phiếu)</span>
        </div>
        <Link to="/factory/packing" className="text-xs font-medium text-indigo-600 hover:underline inline-flex items-center gap-1">
          Mở trang Đóng gói &amp; Nhập kho <ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      {!productionOrderId ? (
        <p className="p-6 text-sm text-slate-500 text-center">Nhật ký đóng gói có sau khi lệnh được tạo, sản xuất và KCS Đạt.</p>
      ) : isLoading ? (
        <p className="p-6 text-sm text-slate-500 text-center">Đang tải nhật ký đóng gói...</p>
      ) : rows.length === 0 ? (
        <p data-testid="po-packaging-log-empty" className="p-6 text-sm text-slate-500 text-center">
          Chưa có phiếu đóng gói nào cho lệnh này — phiếu tự tạo khi lô KCS Đạt, đóng gói tại{" "}
          <Link to="/factory/packing" className="text-indigo-600 hover:underline">Đóng gói &amp; Nhập kho</Link>.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-600">
              <tr>
                <th className="px-3 py-2 font-semibold">Phiếu đóng gói</th>
                <th className="px-3 py-2 font-semibold">Lô KCS</th>
                <th className="px-3 py-2 font-semibold">Ngày KCS</th>
                <th className="px-3 py-2 font-semibold">Số lô</th>
                <th className="px-3 py-2 font-semibold text-right">Nhận từ KCS</th>
                <th className="px-3 py-2 font-semibold text-right">Đạt</th>
                <th className="px-3 py-2 font-semibold text-right">Lỗi</th>
                <th className="px-3 py-2 font-semibold">Người đóng gói</th>
                <th className="px-3 py-2 font-semibold text-right">Thùng</th>
                <th className="px-3 py-2 font-semibold text-right">Pallet</th>
                <th className="px-3 py-2 font-semibold">Phiếu nhập kho</th>
                <th className="px-3 py-2 font-semibold">Trạng thái</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = PACKING_STATUS[r.status] || { label: r.status, cls: "bg-slate-100 text-slate-700 border-slate-200" };
                return (
                  <tr key={r.id} data-testid="po-packaging-log-row" className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono font-semibold">
                      <Link to="/factory/packing" search={{ q: r.code } as any} className="text-indigo-600 hover:underline">
                        {r.code}
                      </Link>
                      {/* Ghi chú phiếu đóng gói (nhập lúc đóng gói) */}
                      {r.notes && (
                        <div className="font-sans font-normal text-[11px] text-amber-800 whitespace-pre-wrap max-w-[220px]" data-testid="po-packaging-note">
                          {r.notes}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Link to="/qc-final/$checkId" params={{ checkId: r.qcFinalId } as any} className="text-slate-700 hover:underline">
                        {r.qcFinal?.finalQcId}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{formatDateTime(r.qcFinal?.checkTime)}</td>
                    <td className="px-3 py-2 text-slate-700">{r.lotNumber || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatNumber(r.qtyFromQc ?? 0)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-medium">{r.status === "PACKED" ? formatNumber(r.qtyPacked ?? 0) : "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-rose-700">{formatNumber(r.qtyDefect ?? 0)}</td>
                    <td className="px-3 py-2 text-slate-700">{r.packer?.fullName || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.totalBoxes ? `${formatNumber(r.totalBoxes)}${r.pcsPerBox ? ` × ${formatNumber(r.pcsPerBox)}` : ""}` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.totalPallets ? formatNumber(r.totalPallets) : "—"}</td>
                    <td className="px-3 py-2 text-slate-700">{r.receiptDocNo || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={cn("inline-flex px-2 py-0.5 rounded-full border text-xs font-medium", st.cls)}>{st.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr data-testid="po-packaging-log-total" className="border-t border-slate-200 bg-slate-50 font-semibold text-slate-800">
                <td className="px-3 py-2" colSpan={4}>Tổng cộng</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatNumber(sum((r) => r.qtyFromQc))}</td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{formatNumber(sum((r) => (r.status === "PACKED" ? r.qtyPacked : 0)))}</td>
                <td className="px-3 py-2 text-right tabular-nums text-rose-700">{formatNumber(sum((r) => r.qtyDefect))}</td>
                <td className="px-3 py-2" />
                <td className="px-3 py-2 text-right tabular-nums">{formatNumber(sum((r) => r.totalBoxes))}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatNumber(sum((r) => r.totalPallets))}</td>
                <td className="px-3 py-2" colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
